import { useEffect, useState } from "react";
import { ExternalLink, Sparkles, Globe } from "lucide-react";
import api from "../lib/api";
import { vibrantTheme } from "./common";

/* Deep-link to a SocialPulse city guide; when none exists, offer an
   AI-generated TravelerPulse guide (cached server-side). */
export const SocialPulseGuide = ({ country, state, city }) => {
  const [link, setLink] = useState(undefined); // undefined=loading, null=none
  const [guide, setGuide] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const theme = vibrantTheme(`${city || state || country}-guide`);

  useEffect(() => {
    if (!country) return;
    api.get("/geo/guide-link", { params: { country, state, city } })
      .then((r) => setLink(r.data.url ? r.data : null))
      .catch(() => setLink(null));
  }, [country, state, city]);

  // Auto-generate the AI guide when no SocialPulse guide exists (no manual click needed).
  useEffect(() => {
    if (link === null && !guide && !busy && !error) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [link]);

  const generate = async () => {
    setBusy(true); setError(null);
    try {
      const r = await api.get("/geo/ai-guide", { params: { country, state, city } });
      setGuide(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "The guide temporarily unavailable — Try again later.");
    } finally {
      setBusy(false);
    }
  };

  if (!country || link === undefined) return null;

  return (
    <section className="border border-zinc-300 bg-white p-5 sm:p-6 border-l-4" style={{ borderLeftColor: theme.bg }}
      data-testid="socialpulse-guide-block">
      <div className="flex items-center gap-2 mb-2">
        <Globe className="w-5 h-5" style={{ color: theme.bg }} />
        <h2 className="font-heading font-900 text-xl">City &amp; Place Guide</h2>
      </div>
      {link ? (
        <a href={link.url} target="_blank" rel="noopener noreferrer" data-testid="socialpulse-guide-link"
          className="inline-flex items-center gap-1.5 font-700 hover:underline" style={{ color: theme.bg }}>
          Full {link.name} guide on SocialPulse <ExternalLink className="w-4 h-4" />
        </a>
      ) : guide ? (
        <div className="space-y-3 text-zinc-700 leading-relaxed" data-testid="ai-city-guide">
          {guide.tagline && <p className="font-heading font-700 text-lg text-foreground">{guide.tagline}</p>}
          {guide.overview && guide.overview.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
          {guide.top_sights?.length > 0 && (
            <ul className="space-y-1">
              {guide.top_sights.map((s) => (
                <li key={s} className="flex gap-2 text-sm"><span style={{ color: theme.bg }}>{"\u2022"}</span>{s}</li>
              ))}
            </ul>
          )}
          {guide.getting_around && <p className="text-sm"><strong>Getting around:</strong> {guide.getting_around}</p>}
          {guide.best_time && <p className="text-sm"><strong>Best time:</strong> {guide.best_time}</p>}
          {guide.local_food && <p className="text-sm"><strong>Local food:</strong> {guide.local_food}</p>}
          <p className="text-xs text-muted-foreground">{guide.disclaimer}</p>
        </div>
      ) : (
        <div>
          <p className="text-sm text-zinc-700 mb-3">No guide exists for this place yet — Click to generate a Traveler Pulse guide.</p>
          <button onClick={generate} disabled={busy} data-testid="generate-ai-guide-btn"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-700 text-white disabled:opacity-60"
            style={{ backgroundColor: theme.bg }}>
            {busy ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Sparkles className="w-4 h-4" />} {busy ? "Generating..." : "Generate city guide"}
          </button>
          {error && <p className="text-sm text-red-600 mt-2" data-testid="ai-guide-error">{error}</p>}
        </div>
      )}
    </section>
  );
};
